import { useEffect, useState } from "react";
import { normaliseSectionColour, SECTION_SWATCHES, sectionLabelColour } from "@keydials/dial-renderer";

export interface SectionColourPickerProps {
  /** The section's colour as stored (`#rrggbb`), or null/undefined for none. */
  value: string | null | undefined;
  /** Called with a lower-case `#rrggbb`, or null for "no colour". */
  onChange: (colour: string | null) => void;
  disabled?: boolean;
  /** The heading above the row. Omitted = "Section colour". */
  label?: string;
}

/** What the picker shows as chosen: none, one of the swatches, or a custom colour outside them. */
export function sectionColourChoice(value: string | null | undefined): "none" | "custom" | number {
  const hex = normaliseSectionColour(value);
  if (!hex) {
    return "none";
  }
  const i = SECTION_SWATCHES.findIndex((s) => s.hex === hex);
  return i >= 0 ? i : "custom";
}

const RING = "0 0 0 2px var(--surface, #fff), 0 0 0 4px var(--text, #1a1a1a)";

/**
 * KEY-755: the colour a section's band is drawn in on the dial — a row of swatches, a custom hex, or none (the
 * platform's own section colour). Shared by the admin editor's Structure tab and the user app's builder, so both
 * offer the same swatches ({@link SECTION_SWATCHES}, a placeholder until the designer's palette lands).
 *
 * The custom box only reports a colour once it is a whole `#rrggbb` (or `#rgb`); until then it holds the typing
 * locally and says what it's waiting for, rather than sending half a colour the server would clear.
 */
export function SectionColourPicker({ value, onChange, disabled, label = "Section colour" }: SectionColourPickerProps) {
  const current = normaliseSectionColour(value);
  const choice = sectionColourChoice(value);
  const [draft, setDraft] = useState(current ?? "");
  useEffect(() => setDraft(current ?? ""), [current]);
  const draftValid = draft.trim() === "" || normaliseSectionColour(draft) !== null;

  const swatch = (hex: string, name: string, chosen: boolean) => (
    <button
      key={hex}
      type="button"
      title={name}
      aria-label={name}
      aria-pressed={chosen}
      disabled={disabled}
      onClick={() => onChange(hex)}
      style={{
        width: 26,
        height: 26,
        borderRadius: 99,
        border: "1px solid rgba(0,0,0,0.15)",
        background: hex,
        boxShadow: chosen ? RING : "none",
        cursor: disabled ? "default" : "pointer",
        padding: 0,
        color: sectionLabelColour(hex),
        fontSize: 13,
        lineHeight: "24px",
      }}
    >
      {chosen ? "✓" : ""}
    </button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-section-colour-picker="">
      <div className="label-overline">{label}</div>
      <div role="group" aria-label={label} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        <button
          type="button"
          aria-pressed={choice === "none"}
          disabled={disabled}
          onClick={() => onChange(null)}
          title="No colour — the dial's own section colour"
          style={{
            height: 26,
            padding: "0 10px",
            borderRadius: 99,
            border: "1px solid var(--border, #d0d5dd)",
            background: "var(--surface, #fff)",
            color: "var(--text, #1a1a1a)",
            fontSize: 12,
            boxShadow: choice === "none" ? RING : "none",
            cursor: disabled ? "default" : "pointer",
          }}
        >
          None
        </button>
        {SECTION_SWATCHES.map((s, i) => swatch(s.hex, s.name, choice === i))}
        {choice === "custom" && current && swatch(current, `Custom ${current}`, true)}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="color"
          aria-label="Pick a custom colour"
          value={current ?? "#888888"}
          disabled={disabled}
          onChange={(e) => onChange(normaliseSectionColour(e.target.value))}
          style={{ width: 32, height: 26, padding: 0, border: "none", background: "none" }}
        />
        <input
          className="field"
          aria-label="Custom colour (hex)"
          placeholder="#rrggbb"
          maxLength={7}
          value={draft}
          disabled={disabled}
          onChange={(e) => {
            const next = e.target.value;
            setDraft(next);
            if (next.trim() === "") {
              onChange(null);
              return;
            }
            const hex = normaliseSectionColour(next);
            if (hex) {
              onChange(hex);
            }
          }}
          style={{ width: 110, fontFamily: "var(--font-mono, monospace)" }}
        />
        <span style={{ fontSize: 12, color: draftValid ? "var(--text-faint, #6b7280)" : "var(--st-red, #b91c1c)" }}>
          {draftValid ? "The status edge still shows on top." : "A colour is # and six hex digits, e.g. #8fb3de."}
        </span>
      </div>
    </div>
  );
}
